/**
 * E2E instrumentation probe.
 *
 * This module is additive: it only *observes* the real client/session/DOM and is only installed when
 * the meeting page is opened with `?e2e=1`. It never changes media, connection or moderation
 * behaviour. The multi-user acceptance harness (`tests/e2e/multiuser/`) reads `window.__nmE2E` to
 * collect, per participant:
 *
 *  - the session state (member list, roles, mute flags) at any point in time;
 *  - a timeline of real SDK events (`connection-state-changed`, `user-joined`, `user-left`,
 *    `user-published`, `user-unpublished`, `user-mute-changed`, `data-received`, ...);
 *  - the real invocation order/arguments/return values/error codes of the public RtcClient methods;
 *  - media-plane truth from the live media transport (LiveKit track publications per remote
 *    participant, including camera vs screen-share sources, track settings and liveness);
 *  - decoded-frame counters of the actual `<video>` elements rendered by React.
 *
 * The probe is intentionally defensive: if an internal media transport shape is unavailable the
 * corresponding field is reported as unavailable instead of throwing.
 */
import { RTC_EVENT_NAMES, type RtcClient } from '@nimeet/rtc-web';
import type { MeetingRole, MeetingSession } from './session';
import type { SceneId } from '../scenarios/scenes';

/* ------------------------------------------------------------------ public types */

export interface ProbeEvent {
  seq: number;
  at: number;
  event: string;
  payload: unknown;
}

export interface ProbeSdkCall {
  seq: number;
  at: number;
  method: string;
  args: unknown;
  ok: boolean;
  durationMs: number;
  result: unknown;
  error: {
    name: string | null;
    code: string | null;
    sdkCode: number | null;
    message: string;
  } | null;
}

export interface ProbeMemberRow {
  uid: number;
  name: string;
  role: string;
  audioMuted: boolean;
  videoMuted: boolean;
  isLocal: boolean;
  handRaised: boolean;
}

export interface ProbeMemberTimelineEntry {
  at: number;
  phase: string;
  count: number;
  members: ProbeMemberRow[];
}

export interface ProbeTrackSettings {
  width: number | null;
  height: number | null;
  frameRate: number | null;
  deviceId: string | null;
}

export interface ProbeLocalTrack {
  source: string;
  sid: string | null;
  kind: string | null;
  muted: boolean | null;
  readyState: string | null;
  enabled: boolean | null;
  settings: ProbeTrackSettings | null;
}

export interface ProbeRemoteTrack {
  source: string;
  kind: string;
  sid: string | null;
  muted: boolean | null;
  subscribed: boolean | null;
  readyState: string | null;
  enabled: boolean | null;
  settings: ProbeTrackSettings | null;
}

export interface ProbeRemoteParticipant {
  uid: number;
  identity: string;
  name: string | null;
  audio: boolean;
  video: boolean;
  tracks: ProbeRemoteTrack[];
}

export interface ProbeMediaPlane {
  available: boolean;
  reason: string | null;
  transportKind: string | null;
  roomState: string | null;
  roomName: string | null;
  localTracks: ProbeLocalTrack[];
  remoteParticipants: ProbeRemoteParticipant[];
  localAudioLevel: number | null;
}

export interface ProbeVideoElement {
  testid: string | null;
  uid: number | null;
  local: boolean;
  width: number;
  height: number;
  paused: boolean;
  readyState: number;
  currentTime: number;
  totalVideoFrames: number;
  droppedVideoFrames: number;
  streamActive: boolean;
  track: {
    id: string;
    readyState: string;
    enabled: boolean;
    muted: boolean;
    settings: ProbeTrackSettings;
  } | null;
}

export interface ProbeAudioSink {
  hasStream: boolean;
  audioTracks: number;
  liveAudioTracks: number;
  muted: boolean;
}

export interface ProbeRuntime {
  at: number;
  connectionState: string;
  role: string | null;
  localUid: number | null;
  channel: string | null;
  publishState: { audio: boolean; video: boolean; screen: boolean };
  clientParticipants: Array<{
    uid: number;
    userId: string | null;
    role: string;
    audio: boolean;
    video: boolean;
  }>;
  mediaPlane: ProbeMediaPlane;
}

export interface ProbeDebugRemoteTrack {
  uid: number;
  sid: string | null;
  source: string;
  kind: string;
  isAdaptiveStream: boolean | null;
  streamState: string | boolean | null;
  muted: boolean | null;
  readyState: string | null;
  settings: ProbeTrackSettings | null;
  attachedElements: number;
}

export interface ProbeReceiverStats {
  uid: number;
  source: string;
  kind: string;
  framesDecoded: number | null;
  framesReceived: number | null;
  framesDropped: number | null;
  frameWidth: number | null;
  frameHeight: number | null;
  bytesReceived: number | null;
  packetsLost: number | null;
  packetsReceived: number | null;
  jitter: number | null;
  mimeType: string | null;
  decoderImplementation: string | null;
}

export interface ProbeSenderStats {
  source: string;
  kind: string;
  framesSent: number | null;
  framesPerSecond: number | null;
  frameWidth: number | null;
  frameHeight: number | null;
  bytesSent: number | null;
  packetsSent: number | null;
  rid: string | null;
  qualityLimitationReason: string | null;
}

export interface ProbeMediaStats {
  received: ProbeReceiverStats[];
  sent: ProbeSenderStats[];
}

export interface ProbeLive {
  at: number;
  phase: string;
  localUid: number | null;
  role: string | null;
  connectionState: string;
  publishState: { audio: boolean; video: boolean; screen: boolean };
  participants: ProbeMemberRow[];
  remote: ProbeRemoteParticipant[];
  localTracks: ProbeLocalTrack[];
  videos: ProbeVideoElement[];
  audioSinks: ProbeAudioSink[];
  roomName: string | null;
  mediaPlaneAvailable: boolean;
}

export interface ProbeSnapshot {
  at: number;
  info: ProbeInfo;
  state: unknown;
  runtime: ProbeRuntime;
  videos: ProbeVideoElement[];
  audioSinks: ProbeAudioSink[];
  events: ProbeEvent[];
  sdkCalls: ProbeSdkCall[];
  memberTimeline: ProbeMemberTimelineEntry[];
}

export interface ProbeInfo {
  scene: SceneId;
  channel: string;
  displayName: string;
  role: MeetingRole;
  startedAt: number;
  userAgent: string;
  pageUrl: string;
  secureContext: boolean;
  offline: boolean;
}

export interface ProbePublishAttempt {
  media: 'audio' | 'video';
  ok: boolean;
  error: {
    name: string | null;
    code: string | null;
    sdkCode: number | null;
    message: string;
  } | null;
}

export interface E2EProbe {
  version: 1;
  info(): ProbeInfo;
  state(): unknown;
  runtime(): ProbeRuntime;
  videos(): ProbeVideoElement[];
  audioSinks(): ProbeAudioSink[];
  events(): ProbeEvent[];
  sdkCalls(): ProbeSdkCall[];
  memberTimeline(): ProbeMemberTimelineEntry[];
  mark(label: string, detail?: unknown): void;
  live(): ProbeLive;
  /** Instrumentation-only: LiveKit adaptive-stream state of every subscribed remote track. */
  debugRemoteTracks(): ProbeDebugRemoteTrack[];
  /** Instrumentation-only repair: attach every subscribed remote video track to its rendered element. */
  attachRemoteVideoTracks(): {
    attached: number;
    details: Array<{ uid: number; source: string; ok: boolean }>;
  };
  /** WebRTC receiver/sender counters straight from the media transport (no DOM involvement). */
  mediaStats(): Promise<ProbeMediaStats>;
  snapshot(): ProbeSnapshot;
  /** Deliberately attempt a publish as the current (possibly audience) participant. */
  attemptPublish(media: 'audio' | 'video'): Promise<ProbePublishAttempt>;
}

declare global {
  interface Window {
    __nmE2E?: E2EProbe;
  }
}

/* ------------------------------------------------------------------ helpers */

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function asRecord(value: unknown): UnknownRecord {
  return isRecord(value) ? value : {};
}

function describeError(error: unknown): {
  name: string | null;
  code: string | null;
  sdkCode: number | null;
  message: string;
} {
  const record = asRecord(error);
  const name =
    typeof record.name === 'string' ? record.name : error instanceof Error ? error.name : null;
  const code = typeof record.code === 'string' ? record.code : null;
  const sdkCode = typeof record.sdkCode === 'number' ? record.sdkCode : null;
  const message = typeof record.message === 'string' ? record.message : String(error);
  return { name, code, sdkCode, message };
}

function settingsOf(track: MediaStreamTrack | null | undefined): ProbeTrackSettings | null {
  if (!track || typeof track.getSettings !== 'function') return null;
  const settings = track.getSettings();
  return {
    width: typeof settings.width === 'number' ? settings.width : null,
    height: typeof settings.height === 'number' ? settings.height : null,
    frameRate: typeof settings.frameRate === 'number' ? settings.frameRate : null,
    deviceId: typeof settings.deviceId === 'string' ? settings.deviceId : null,
  };
}

function decodePayload(value: Uint8Array): unknown {
  try {
    const text = new TextDecoder().decode(value);
    try {
      return { bytes: value.length, json: JSON.parse(text) };
    } catch {
      return { bytes: value.length, text };
    }
  } catch {
    return { bytes: value.length };
  }
}

/** JSON-safe argument projection: media payloads become length+topic, tokens are never recorded. */
function projectArgs(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value ?? null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
    return value;
  if (value instanceof Uint8Array) return decodePayload(value);
  if (Array.isArray(value)) return value.slice(0, 16).map((entry) => projectArgs(entry, depth + 1));
  if (depth > 3) return '[depth]';
  if (isRecord(value)) {
    const out: UnknownRecord = {};
    for (const [key, entry] of Object.entries(value)) {
      if (key === 'rtcToken' || key === 'token') {
        out[key] = '[redacted]';
        continue;
      }
      out[key] = projectArgs(entry, depth + 1);
    }
    return out;
  }
  return String(value);
}

/* Media-plane internals (LiveKit publications). Structural types, never imported from the adapter. */

interface MediaTrackLike {
  mediaStreamTrack?: MediaStreamTrack | null;
  isAdaptiveStream?: boolean;
  streamState?: unknown;
  attachedElements?: unknown[];
  attach?: (element: HTMLMediaElement) => HTMLMediaElement;
  getReceiverStats?: () => Promise<UnknownRecord | undefined>;
  getSenderStats?: () => Promise<UnknownRecord | UnknownRecord[] | undefined>;
}

interface MediaPublicationLike {
  trackSid?: string;
  kind?: string;
  source?: string;
  isMuted?: boolean;
  isSubscribed?: boolean;
  track?: MediaTrackLike | null;
}

interface MediaParticipantLike {
  identity?: string;
  name?: string | null;
  audioLevel?: number;
  trackPublications?: Map<string, MediaPublicationLike>;
}

interface MediaRoomLike {
  state?: string;
  name?: string;
  localParticipant?: MediaParticipantLike;
  remoteParticipants?: Map<string, MediaParticipantLike>;
}

function publicationsOf(participant: MediaParticipantLike | undefined): MediaPublicationLike[] {
  const publications = participant?.trackPublications;
  if (!publications || typeof publications.values !== 'function') return [];
  return [...publications.values()];
}

function trackFromPublication(publication: MediaPublicationLike): MediaStreamTrack | null {
  const track = publication.track?.mediaStreamTrack;
  return track ?? null;
}

/* ------------------------------------------------------------------ install */

export function installE2EProbe(input: {
  client: RtcClient;
  session: MeetingSession;
  scene: SceneId;
  channel: string;
  displayName: string;
  role: MeetingRole;
  offline: boolean;
}): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (params.get('e2e') !== '1') return;
  if (window.__nmE2E) return;

  const { client, session } = input;
  const startedAt = Date.now();
  const events: ProbeEvent[] = [];
  const sdkCalls: ProbeSdkCall[] = [];
  const memberTimeline: ProbeMemberTimelineEntry[] = [];
  let eventSeq = 0;
  let callSeq = 0;

  const info: ProbeInfo = {
    scene: input.scene,
    channel: input.channel,
    displayName: input.displayName,
    role: input.role,
    startedAt,
    userAgent: navigator.userAgent,
    pageUrl: window.location.href,
    secureContext: window.isSecureContext,
    offline: input.offline,
  };

  const push = (event: string, payload: unknown): void => {
    eventSeq += 1;
    events.push({ seq: eventSeq, at: Date.now(), event, payload: projectArgs(payload) });
    if (events.length > 5000) events.splice(0, events.length - 5000);
  };

  // Real SDK events, one listener per event name, attached before session.join() runs.
  const observable = client as unknown as {
    on: (event: string, listener: (payload: unknown) => void) => () => void;
  };
  for (const name of RTC_EVENT_NAMES) {
    try {
      observable.on(name, (payload) => push(name, payload));
    } catch {
      /* older client build without this event: reported as absent by the empty event log */
    }
  }

  // Member-list timeline: every observable roster change, with names/roles/mute flags.
  const memberSignature = (): string => {
    const state = session.getState();
    return state.participants
      .map(
        (participant) =>
          `${participant.uid}:${participant.displayName}:${participant.role}:${participant.audioMuted}:${participant.videoMuted}:${participant.isLocal}`,
      )
      .sort()
      .join('|');
  };
  let lastMemberSignature = '';
  let lastPhase = '';
  session.subscribe((state) => {
    const signature = memberSignature();
    if (signature === lastMemberSignature && state.phase === lastPhase) return;
    lastMemberSignature = signature;
    lastPhase = state.phase;
    memberTimeline.push({
      at: Date.now(),
      phase: state.phase,
      count: state.participants.length,
      members: state.participants.map((participant) => ({
        uid: participant.uid,
        name: participant.displayName,
        role: participant.role,
        audioMuted: participant.audioMuted,
        videoMuted: participant.videoMuted,
        isLocal: participant.isLocal,
        handRaised: participant.handRaised,
      })),
    });
    if (memberTimeline.length > 1000) memberTimeline.splice(0, memberTimeline.length - 1000);
  });

  // Real RtcClient call log: invocation order, arguments, return value or error code.
  const SDK_METHODS = [
    'joinChannel',
    'leaveChannel',
    'renewToken',
    'muteAudio',
    'muteVideo',
    'publishAudio',
    'publishVideo',
    'unpublishAudio',
    'unpublishVideo',
    'startScreenShare',
    'stopScreenShare',
    'setRole',
    'sendData',
    'getStats',
    'setVideoEncoderConfiguration',
  ] as const;

  for (const method of SDK_METHODS) {
    const target = client as unknown as UnknownRecord;
    const original = target[method];
    if (typeof original !== 'function') continue;
    const call = original as (...args: unknown[]) => unknown;
    const wrapped = (...args: unknown[]): unknown => {
      callSeq += 1;
      const seq = callSeq;
      const at = Date.now();
      const projected = projectArgs(args);
      const record = (ok: boolean, result: unknown, error: ProbeSdkCall['error']): void => {
        sdkCalls.push({
          seq,
          at,
          method,
          args: projected,
          ok,
          durationMs: Date.now() - at,
          result: projectArgs(result),
          error,
        });
        if (sdkCalls.length > 2000) sdkCalls.splice(0, sdkCalls.length - 2000);
      };
      try {
        const result = call.apply(client, args);
        if (
          result !== null &&
          typeof result === 'object' &&
          typeof (result as { then?: unknown }).then === 'function'
        ) {
          return (result as Promise<unknown>).then(
            (value) => {
              record(true, value ?? null, null);
              return value;
            },
            (error: unknown) => {
              record(false, null, describeError(error));
              throw error;
            },
          );
        }
        record(true, result ?? null, null);
        return result;
      } catch (error) {
        record(false, null, describeError(error));
        throw error;
      }
    };
    try {
      Object.defineProperty(client, method, { value: wrapped, configurable: true, writable: true });
    } catch {
      /* non-configurable method: call log simply misses it */
    }
  }

  const mediaPlane = (): ProbeMediaPlane => {
    try {
      const clientAny = client as unknown as {
        transport?: { kind?: string; room?: MediaRoomLike | null };
      };
      const transport = clientAny.transport;
      const room = transport?.room ?? null;
      if (!room) {
        return {
          available: false,
          reason: 'media transport not connected yet',
          transportKind: transport?.kind ?? null,
          roomState: null,
          roomName: null,
          localTracks: [],
          remoteParticipants: [],
          localAudioLevel: null,
        };
      }
      const localTracks: ProbeLocalTrack[] = publicationsOf(room.localParticipant).map(
        (publication) => {
          const track = trackFromPublication(publication);
          return {
            source: String(publication.source ?? 'unknown'),
            sid: publication.trackSid ?? null,
            kind: publication.kind ?? null,
            muted: typeof publication.isMuted === 'boolean' ? publication.isMuted : null,
            readyState: track?.readyState ?? null,
            enabled: track?.enabled ?? null,
            settings: settingsOf(track),
          };
        },
      );
      const remoteParticipants: ProbeRemoteParticipant[] = [];
      const remote = room.remoteParticipants;
      if (remote && typeof remote.values === 'function') {
        for (const [identity, participant] of remote) {
          const uid = Number.parseInt(identity, 10);
          const tracks: ProbeRemoteTrack[] = publicationsOf(participant).map((publication) => {
            const track = trackFromPublication(publication);
            return {
              source: String(publication.source ?? 'unknown'),
              kind: String(publication.kind ?? 'unknown'),
              sid: publication.trackSid ?? null,
              muted: typeof publication.isMuted === 'boolean' ? publication.isMuted : null,
              subscribed:
                typeof publication.isSubscribed === 'boolean' ? publication.isSubscribed : null,
              readyState: track?.readyState ?? null,
              enabled: track?.enabled ?? null,
              settings: settingsOf(track),
            };
          });
          remoteParticipants.push({
            uid: Number.isInteger(uid) ? uid : 0,
            identity,
            name: participant.name ?? null,
            audio: tracks.some((track) => track.kind === 'audio'),
            video: tracks.some((track) => track.kind === 'video'),
            tracks,
          });
        }
      }
      return {
        available: true,
        reason: null,
        transportKind: transport?.kind ?? null,
        roomState: room.state ?? null,
        roomName: room.name ?? null,
        localTracks,
        remoteParticipants,
        localAudioLevel:
          typeof room.localParticipant?.audioLevel === 'number'
            ? room.localParticipant.audioLevel
            : null,
      };
    } catch (error) {
      return {
        available: false,
        reason: describeError(error).message,
        transportKind: null,
        roomState: null,
        roomName: null,
        localTracks: [],
        remoteParticipants: [],
        localAudioLevel: null,
      };
    }
  };

  const runtime = (): ProbeRuntime => {
    const clientAny = client as unknown as {
      getConnectionState?: () => string;
      getRole?: () => string;
      getLocalUid?: () => number | null;
      getChannelName?: () => string | null;
      getPublishState?: () => { audio: boolean; video: boolean; screen: boolean };
      getParticipantsSnapshot?: () => Array<{
        uid: number;
        userId: string | null;
        role: string;
        audio: boolean;
        video: boolean;
      }>;
    };
    return {
      at: Date.now(),
      connectionState: clientAny.getConnectionState?.() ?? 'unknown',
      role: clientAny.getRole?.() ?? null,
      localUid: clientAny.getLocalUid?.() ?? null,
      channel: clientAny.getChannelName?.() ?? null,
      publishState: clientAny.getPublishState?.() ?? { audio: false, video: false, screen: false },
      clientParticipants: clientAny.getParticipantsSnapshot?.() ?? [],
      mediaPlane: mediaPlane(),
    };
  };

  const videos = (): ProbeVideoElement[] => {
    const elements = [...document.querySelectorAll('video')];
    return elements.map((element) => {
      const tile = element.closest('[data-testid^="tile-"]');
      const testid = tile?.getAttribute('data-testid') ?? null;
      const local = tile?.getAttribute('data-local') === 'true';
      const stream = element.srcObject instanceof MediaStream ? element.srcObject : null;
      const track = stream?.getVideoTracks()[0] ?? null;
      const quality =
        typeof element.getVideoPlaybackQuality === 'function'
          ? element.getVideoPlaybackQuality()
          : null;
      const uidText = testid?.startsWith('tile-') ? testid.slice('tile-'.length) : '';
      const uid = uidText && Number.isInteger(Number(uidText)) ? Number(uidText) : null;
      return {
        testid,
        uid,
        local,
        width: element.videoWidth,
        height: element.videoHeight,
        paused: element.paused,
        readyState: element.readyState,
        currentTime: element.currentTime,
        totalVideoFrames: quality?.totalVideoFrames ?? 0,
        droppedVideoFrames: quality?.droppedVideoFrames ?? 0,
        streamActive: stream?.active ?? false,
        track: track
          ? {
              id: track.id,
              readyState: track.readyState,
              enabled: track.enabled,
              muted: track.muted,
              settings: settingsOf(track) ?? {
                width: null,
                height: null,
                frameRate: null,
                deviceId: null,
              },
            }
          : null,
      };
    });
  };

  const audioSinks = (): ProbeAudioSink[] => {
    const elements = [...document.querySelectorAll('audio')];
    return elements.map((element) => {
      const stream = element.srcObject instanceof MediaStream ? element.srcObject : null;
      const tracks = stream?.getAudioTracks() ?? [];
      return {
        hasStream: stream !== null,
        audioTracks: tracks.length,
        liveAudioTracks: tracks.filter((track) => track.readyState === 'live').length,
        muted: tracks.every((track) => track.muted),
      };
    });
  };

  const debugRemoteTracks = (): ProbeDebugRemoteTrack[] => {
    try {
      const room =
        (client as unknown as { transport?: { room?: MediaRoomLike | null } }).transport?.room ??
        null;
      const remote = room?.remoteParticipants;
      if (!remote || typeof remote.values !== 'function') return [];
      const out: ProbeDebugRemoteTrack[] = [];
      for (const [identity, participant] of remote) {
        const uidText = Number.parseInt(identity, 10);
        const uid = Number.isInteger(uidText) ? uidText : 0;
        for (const publication of publicationsOf(participant)) {
          const track = publication.track ?? null;
          const mediaTrack = trackFromPublication(publication);
          out.push({
            uid,
            sid: publication.trackSid ?? null,
            source: String(publication.source ?? 'unknown'),
            kind: String(publication.kind ?? 'unknown'),
            isAdaptiveStream:
              typeof track?.isAdaptiveStream === 'boolean' ? track.isAdaptiveStream : null,
            streamState:
              typeof track?.streamState === 'string' || typeof track?.streamState === 'boolean'
                ? (track.streamState as string | boolean)
                : typeof track?.streamState === 'number'
                  ? String(track.streamState)
                  : null,
            muted: typeof publication.isMuted === 'boolean' ? publication.isMuted : null,
            readyState: mediaTrack?.readyState ?? null,
            settings: settingsOf(mediaTrack),
            attachedElements: Array.isArray(track?.attachedElements)
              ? track.attachedElements.length
              : 0,
          });
        }
      }
      return out;
    } catch {
      return [];
    }
  };

  const attachRemoteVideoTracks = (): {
    attached: number;
    details: Array<{ uid: number; source: string; ok: boolean }>;
  } => {
    const details: Array<{ uid: number; source: string; ok: boolean }> = [];
    let attached = 0;
    try {
      const room =
        (client as unknown as { transport?: { room?: MediaRoomLike | null } }).transport?.room ??
        null;
      const remote = room?.remoteParticipants;
      if (!remote || typeof remote.values !== 'function') return { attached, details };
      for (const [identity, participant] of remote) {
        const uid = Number.parseInt(identity, 10);
        for (const publication of publicationsOf(participant)) {
          if (publication.kind !== 'video') continue;
          const track = publication.track ?? null;
          if (!track?.attach) continue;
          const element = document.querySelector(`[data-testid="tile-${uid}"] video`);
          if (!(element instanceof HTMLMediaElement)) {
            details.push({ uid, source: String(publication.source ?? 'unknown'), ok: false });
            continue;
          }
          try {
            track.attach(element);
            attached += 1;
            details.push({ uid, source: String(publication.source ?? 'unknown'), ok: true });
          } catch {
            details.push({ uid, source: String(publication.source ?? 'unknown'), ok: false });
          }
        }
      }
    } catch {
      /* diagnostics only */
    }
    return { attached, details };
  };

  const mediaStats = async (): Promise<ProbeMediaStats> => {
    const numberOrNull = (value: unknown): number | null =>
      typeof value === 'number' && Number.isFinite(value) ? value : null;
    const stringOrNull = (value: unknown): string | null =>
      typeof value === 'string' ? value : null;
    const received: ProbeReceiverStats[] = [];
    const sent: ProbeSenderStats[] = [];
    try {
      const room =
        (client as unknown as { transport?: { room?: MediaRoomLike | null } }).transport?.room ??
        null;
      if (!room) return { received, sent };
      const remote = room.remoteParticipants;
      if (remote && typeof remote.values === 'function') {
        for (const [identity, participant] of remote) {
          const uid = Number.parseInt(identity, 10);
          for (const publication of publicationsOf(participant)) {
            const track = publication.track ?? null;
            if (!track?.getReceiverStats) continue;
            const stats = (await track.getReceiverStats().catch(() => undefined)) ?? null;
            const record = asRecord(stats);
            received.push({
              uid: Number.isInteger(uid) ? uid : 0,
              source: String(publication.source ?? 'unknown'),
              kind: String(publication.kind ?? 'unknown'),
              framesDecoded: numberOrNull(record.framesDecoded),
              framesReceived: numberOrNull(record.framesReceived),
              framesDropped: numberOrNull(record.framesDropped),
              frameWidth: numberOrNull(record.frameWidth),
              frameHeight: numberOrNull(record.frameHeight),
              bytesReceived: numberOrNull(record.bytesReceived),
              packetsLost: numberOrNull(record.packetsLost),
              packetsReceived: numberOrNull(record.packetsReceived),
              jitter: numberOrNull(record.jitter),
              mimeType: stringOrNull(record.mimeType),
              decoderImplementation: stringOrNull(record.decoderImplementation),
            });
          }
        }
      }
      for (const publication of publicationsOf(room.localParticipant)) {
        const track = publication.track ?? null;
        if (!track?.getSenderStats) continue;
        const stats = (await track.getSenderStats().catch(() => undefined)) ?? null;
        const list = Array.isArray(stats) ? stats : stats ? [stats] : [];
        const record = asRecord(
          list.find((entry) => numberOrNull(asRecord(entry).frameWidth) !== null) ?? list[0],
        );
        sent.push({
          source: String(publication.source ?? 'unknown'),
          kind: String(publication.kind ?? 'unknown'),
          framesSent: numberOrNull(record.framesSent),
          framesPerSecond: numberOrNull(record.framesPerSecond),
          frameWidth: numberOrNull(record.frameWidth),
          frameHeight: numberOrNull(record.frameHeight),
          bytesSent: numberOrNull(record.bytesSent),
          packetsSent: numberOrNull(record.packetsSent),
          rid: stringOrNull(record.rid),
          qualityLimitationReason: stringOrNull(record.qualityLimitationReason),
        });
      }
    } catch {
      /* diagnostics only */
    }
    return { received, sent };
  };

  const probe: E2EProbe = {
    version: 1,
    info: () => info,
    state: () => session.getState(),
    runtime,
    videos,
    audioSinks,
    events: () => events,
    sdkCalls: () => sdkCalls,
    memberTimeline: () => memberTimeline,
    mark: (label, detail) => push(`probe:${label}`, detail ?? null),
    live: () => {
      const state = session.getState();
      const media = mediaPlane();
      return {
        at: Date.now(),
        phase: state.phase,
        localUid: state.localUid,
        role: client.getRole(),
        connectionState: client.getConnectionState(),
        publishState: client.getPublishState(),
        participants: state.participants.map((participant) => ({
          uid: participant.uid,
          name: participant.displayName,
          role: participant.role,
          audioMuted: participant.audioMuted,
          videoMuted: participant.videoMuted,
          isLocal: participant.isLocal,
          handRaised: participant.handRaised,
        })),
        remote: media.remoteParticipants,
        localTracks: media.localTracks,
        videos: videos(),
        audioSinks: audioSinks(),
        roomName: media.roomName,
        mediaPlaneAvailable: media.available,
      };
    },
    debugRemoteTracks,
    attachRemoteVideoTracks,
    mediaStats,
    snapshot: () => ({
      at: Date.now(),
      info,
      state: session.getState(),
      runtime: runtime(),
      videos: videos(),
      audioSinks: audioSinks(),
      events: [...events],
      sdkCalls: [...sdkCalls],
      memberTimeline: [...memberTimeline],
    }),
    attemptPublish: async (media) => {
      try {
        if (media === 'audio') await client.publishAudio();
        else await client.publishVideo();
        return { media, ok: true, error: null };
      } catch (error) {
        return { media, ok: false, error: describeError(error) };
      }
    },
  };

  window.__nmE2E = probe;
}
